import GlobalTextInputField from "../../inputs/GlobalTextInputField";
import {
  ReactElement,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsxm } from "../../../utils/clxm/clxm";
import { useClickOutside } from "../../../utils/hooks/useClickOutSide";
import { useForkRef } from "../../../utils/hooks/useForkRef";
import { PSelectOption, SelectOption } from "./SelectOption";
import { ArrowDownIcon } from "./icons/ArrowDownIcon";

export const INPUT_HEIGHT_CLS = "h-[34px]";
export const INPUT_FONT_CLS = "text-[12px]";

export type PSelect = {
  options: PSelectOption["option"][];
  value?: string | number;
  onChange: (value: string | number) => void;
  getSelectCls?: (expanded: boolean) => string;
  getOptionCls?: (isActive: boolean) => string;
  getOptionWrapperCls?: () => string;
  getSelectedOptionCls?: () => string;
  getSelectedValueCls?: string;
  label?: string;
  color?: string;
  baseColor?: string;
  borderColor?: string;
  expandedBorderColor?: string;
  titleColor?: string;
  optionsHeader?: ReactElement;
  displaySelectedValue?: string | ReactElement;
  enableSearch?: boolean;
  searchableLabels?: any;
  onExpandedChange?: (expanded: boolean) => void;
  expanded?: boolean;
  dropdownMaxHeight?: string;
  required?: boolean;
  fontWeight?: string;
  placeholder?: string;
} & Pick<PSelectOption, "getOptionCls">;

export const getRotateButtonCls = (expanded: boolean) =>
  clsxm(
    "text-text-subtle s-[16px] origin-center transition-all duration-400",
    expanded ? "rotate-180" : "rotate-0"
  );

export const Select = forwardRef<
  HTMLInputElement,
  PSelect & { expanded?: boolean }
>(function SelectInput(
  {
    options,
    onChange,
    value,
    getSelectCls,
    getOptionCls,
    getOptionWrapperCls,
    getSelectedOptionCls,
    label,
    optionsHeader,
    displaySelectedValue,
    color = "bg-page-body",
    baseColor = "bg-page-body",
    borderColor = "border-border-subtle",
    expandedBorderColor = "border-border-subtle",
    titleColor = "text-text-subtle",
    enableSearch,
    searchableLabels,
    onExpandedChange,
    expanded: controlledExpanded,
    dropdownMaxHeight,
    required = false,
    fontWeight = "font-normal",
    placeholder = "Select",
  },
  ref
) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  const [searchQuery, setSearchQuery] = useState("");

  const handleSelect = (nextValue: string | number) => {
    setExpanded(false);
    onChange && onChange(nextValue);
  };

  const divRef = useClickOutside<HTMLDivElement>({
    handler: () => setExpanded(false),
  });
  const rootRef = useForkRef<HTMLDivElement>(divRef, ref);

  const hasValue = value !== undefined && value !== null && value !== "";

  const displayValue = useMemo(() => {
    if (!hasValue) {
      return (
        <span className="text-text-subtle text-[12px]">{placeholder}</span>
      );
    }
    const selectedItem = options.find((item) => item.value === value);
    return selectedItem?.selectedLabel || selectedItem?.label || placeholder;
  }, [value, options, hasValue, placeholder]);

  const fieldRef = useRef<HTMLElement | null>(null);

  const filteredOptions = useMemo(() => {
    if (searchQuery.trim() === "" || !searchableLabels) return options;
    return options.filter((option, index) => {
      const labelsToSearch = searchableLabels[index];
      return labelsToSearch?.some((labelText: string) =>
        labelText.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [searchQuery, options, searchableLabels]);

  const handleExpandToggle = (newExpanded: boolean) => {
    setExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  return (
    <div className="flex flex-col w-full relative">
      {label && (
        <>
          <div className="flex items-center gap-2">
            <span className={clsxm("font-normal text-[10px]", titleColor)}>
              {label}
            </span>
            {required && (
              <span
                className={`text-[8px] font-semibold px-2 py-[2px] rounded-full transition-colors ${
                  hasValue
                    ? "bg-bg-status-green text-text-base"
                    : "bg-bg-status-red text-text-base"
                }`}
              >
                REQ
              </span>
            )}
          </div>
          <div className="my-[3px]" />
        </>
      )}
      <div
        ref={rootRef}
        role="button"
        className="relative flex flex-col w-full"
        onClick={() => handleExpandToggle(!expanded)}
      >
        <div
          className={clsxm(
            "flex flex-col justify-between items-center border overflow-hidden",
            expanded
              ? `${expandedBorderColor} rounded-t-lg rounded-b-none`
              : `${borderColor} rounded-lg`,
            expanded && "shadow-lg"
          )}
        >
          <div
            className={clsxm(
              "flex flex-row justify-between items-center",
              INPUT_HEIGHT_CLS,
              INPUT_FONT_CLS,
              `px-[10px] w-full text-text-base bg-${
                expanded ? color : baseColor
              } gap-3`,
              // Ensure Tailwind picks up these bg utilities
              expanded ? "bg-bg-page-body" : "bg-bg-page-body",
              getSelectCls && getSelectCls(expanded)
            )}
            ref={(node) => {
              if (node) {
                fieldRef.current = node;
              }
            }}
          >
            <div
              className={clsxm(
                `w-full text-text-base ${fontWeight} text-left text-[12px]`,
                getSelectedOptionCls && getSelectedOptionCls()
              )}
            >
              {displaySelectedValue || displayValue}
            </div>
            <ArrowDownIcon className={getRotateButtonCls(expanded)} />
          </div>

          {expanded && (
            <div
              className={clsxm(
                `absolute border border-t-0 ${expandedBorderColor} left-0 rounded-b-lg flex flex-col w-full z-[9999] shadow-lg`,
                getOptionWrapperCls && getOptionWrapperCls()
              )}
              style={{
                top: fieldRef.current?.clientHeight ?? "100%",
                backgroundColor: "var(--bg-page-body)",
                maxHeight: dropdownMaxHeight || "40vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {optionsHeader}
              {enableSearch && (
                <div className="p-[10px]">
                  <GlobalTextInputField
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    onClick={(e) => e.stopPropagation()}
                    titleColor="text-text-subtle"
                  />
                </div>
              )}
              {filteredOptions.map((item, index) => (
                <div key={item.value}>
                  <SelectOption
                    onSelect={handleSelect}
                    styles={{
                      height: fieldRef.current?.clientHeight
                        ? fieldRef.current?.clientHeight
                        : "34px",
                    }}
                    isActive={item.value === value}
                    getOptionCls={getOptionCls}
                    option={item}
                    onExpandedChange={onExpandedChange}
                  />
                  {index < filteredOptions.length - 1 && (
                    <div className="border-t border-border-subtle" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
